import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type Particle = {
  el: SVGPathElement;
  // base position in SVG user coords
  baseUx: number;
  baseUy: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  mass: number;          // affects how much the particle resists movement
  sensitivity: number;   // multiplier for repulsion force
  springK: number;       // individual spring constant
  damping: number;       // individual damping factor
};

class BlockQuote {
  private blockquote: HTMLElement;
  private bgContainer: HTMLElement | null = null;
  private svgPattern: SVGSVGElement | null = null;
  private topSvg: SVGElement | null = null;
  private bottomSvg: SVGElement | null = null;
  private paths: SVGPathElement[] = [];
  private particles: Particle[] = [];
  private mouseX: number = window.innerWidth / 2;
  private mouseY: number = window.innerHeight / 2;
  private rafId: number | null = null;
  private lastT = 0;
  private invCTM: DOMMatrix | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private scrollProg: number = 0; // 0..1 from ScrollTrigger for svg scroll-parallax

  constructor(blockquoteEl: HTMLElement) {
    this.blockquote = blockquoteEl;
    this.bgContainer = this.blockquote.querySelector<HTMLElement>(".blockquoteBg");
    this.svgPattern = this.bgContainer?.querySelector<SVGSVGElement>(".svgquotepattern") ?? null;

    // Select top and bottom SVGs more reliably by their position classes
    this.topSvg = this.blockquote.querySelector<SVGElement>("svg.quoteBlockStart") ?? null;
    this.bottomSvg = this.blockquote.querySelector<SVGElement>("svg.quoteBlockEnd") ?? null;

    if (this.svgPattern) {
      this.paths = Array.from(this.svgPattern.querySelectorAll("path"));
    }

    this.onPointer = this.onPointer.bind(this);
    this.animate = this.animate.bind(this);
    this.recomputeBasePositions = this.recomputeBasePositions.bind(this);
  }

  public init() {
    if (!this.blockquote) return;

    gsap.set(this.blockquote, { position: "relative", zIndex: 10 });

    // horizontal scroll motion for the two small mask svgs (mouse won't affect these)
    if (this.topSvg) {
      // Set initial Y offset to prevent gaps from sub-pixel rendering
      gsap.set(this.topSvg, { y: -1 });
      gsap.to(this.topSvg, {
        xPercent: -50,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: this.blockquote,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }

    if (this.bottomSvg) {
      // Set initial Y offset to prevent gaps from sub-pixel rendering
      gsap.set(this.bottomSvg, { y: 1 });
      gsap.to(this.bottomSvg, {
        xPercent: 50,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: this.blockquote,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }

    // Instead of animating svgPattern with CSS transforms (which breaks screen<->SVG mapping),
    // use a ScrollTrigger that updates a numeric progress value. We'll apply actual SVG attribute
    // transforms in the RAF loop so the mapping between screen and SVG user coords stays consistent.
    if (this.svgPattern) {
      ScrollTrigger.create({
        trigger: this.blockquote,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          this.scrollProg = self.progress;
        },
        onRefresh: this.recomputeBasePositions,
      });
    }

    // build particles & compute base positions
    this.buildParticles();

    // pointer tracking (per-instance handlers)
    document.addEventListener("mousemove", this.onPointer, { passive: true });
    document.addEventListener("touchmove", this.onPointer, { passive: true });

    // observe resizes of the SVG pattern and the blockquote to recompute coords
    if (this.svgPattern && "ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.recomputeBasePositions);
      this.resizeObserver.observe(this.svgPattern);
      this.resizeObserver.observe(this.blockquote);
    }
    window.addEventListener("resize", this.recomputeBasePositions, { passive: true });

    // start RAF loop
    this.lastT = performance.now();
    if (!this.rafId) this.rafId = requestAnimationFrame(this.animate);
  }

  private buildParticles() {
    this.particles = [];
    if (!this.svgPattern || this.paths.length === 0) return;
    // compute current inverse CTM for screen->svg conversions
    try {
      const ctm = this.svgPattern.getScreenCTM();
      this.invCTM = ctm ? ctm.inverse() : null;
    } catch {
      this.invCTM = null;
    }

    for (const p of this.paths) {
      const r = p.getBoundingClientRect();
      const screenCx = r.left + r.width / 2;
      const screenCy = r.top + r.height / 2;

      let baseUx = screenCx;
      let baseUy = screenCy;
      if (this.invCTM && this.svgPattern) {
        const pt = this.svgPattern.createSVGPoint();
        pt.x = screenCx;
        pt.y = screenCy;
        const svgPt = pt.matrixTransform(this.invCTM);
        baseUx = svgPt.x;
        baseUy = svgPt.y;
      }

      const radius = Math.max(20, Math.min(120, Math.max(r.width, r.height) / 2));
      // reset SVG transform attribute (reliable)
      p.removeAttribute("transform");
      p.setAttribute("transform", `translate(0,0)`);
      p.style.willChange = "transform";

      // Give each particle unique physical properties for varied behavior
      const mass = 0.3 + Math.random() * 1.0;           // 0.3 to 1.3 (lighter = moves more)
      const sensitivity = 0.8 + Math.random() * 1.2;    // 0.8 to 2.0 (higher variation)
      const springK = 20 + Math.random() * 40;          // 20 to 60 (some very springy, some slow)
      const damping = 0.70 + Math.random() * 0.20;      // 0.70 to 0.90 (more bouncy particles)

      this.particles.push({
        el: p,
        baseUx,
        baseUy,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        radius,
        mass,
        sensitivity,
        springK,
        damping,
      });
    }
  }

  // recompute base positions (call on resize/refresh)
  private recomputeBasePositions() {
    if (!this.svgPattern || this.particles.length === 0) {
      // if particles not initialized yet, build them
      this.buildParticles();
      return;
    }

    try {
      const ctm = this.svgPattern.getScreenCTM();
      this.invCTM = ctm ? ctm.inverse() : null;
    } catch {
      this.invCTM = null;
    }

    for (const part of this.particles) {
      const bbox = part.el.getBoundingClientRect();
      const screenCx = bbox.left + bbox.width / 2;
      const screenCy = bbox.top + bbox.height / 2;
      if (this.invCTM && this.svgPattern) {
        const pt = this.svgPattern.createSVGPoint();
        pt.x = screenCx;
        pt.y = screenCy;
        const svgPt = pt.matrixTransform(this.invCTM);
        part.baseUx = svgPt.x;
        part.baseUy = svgPt.y;
      } else {
        part.baseUx = screenCx;
        part.baseUy = screenCy;
      }
      // reset dynamic state so changes don't produce a jump
      part.x = 0;
      part.y = 0;
      part.vx = 0;
      part.vy = 0;
      part.el.setAttribute("transform", `translate(0,0)`);
    }
  }

  private onPointer(e: MouseEvent | TouchEvent) {
    if ("touches" in e && e.touches.length) {
      this.mouseX = e.touches[0].clientX;
      this.mouseY = e.touches[0].clientY;
    } else {
      this.mouseX = (e as MouseEvent).clientX;
      this.mouseY = (e as MouseEvent).clientY;
    }
  }

  // animate() is invoked by requestAnimationFrame and re-schedules itself each frame
  private animate(t?: number) {
    const now = t ?? performance.now();
    let dt = (now - this.lastT) / 1000;
    this.lastT = now;
    dt = Math.min(0.033, Math.max(0.008, dt)); // clamp dt

    // physics parameters (tweak to taste)
    // NOTE: SVG has viewBox="0 0 1510 803" so these are in that coordinate space
    const influenceRadius = 800; // in SVG user units - much wider for viewBox scale
    const maxDisp = 300; // much larger max displacement for visible movement in viewBox
    const baseRepulsion = 150000; // MUCH higher to account for viewBox scale

    // compute fresh inverse CTM each frame so screen<->SVG mapping stays correct
    let localInv: DOMMatrix | null = null;
    if (this.svgPattern) {
      try {
        const ctm = this.svgPattern.getScreenCTM();
        localInv = ctm ? ctm.inverse() : null;
      } catch {
        localInv = null;
      }
    }

    // convert mouse to SVG user coords (so math runs in same space)
    let mouseUx = this.mouseX;
    let mouseUy = this.mouseY;
    if (this.svgPattern && localInv) {
      try {
        const pt = this.svgPattern.createSVGPoint();
        pt.x = this.mouseX;
        pt.y = this.mouseY;
        const svgPt = pt.matrixTransform(localInv);
        mouseUx = svgPt.x;
        mouseUy = svgPt.y;
      } catch {
        // fallback: keep client coords
      }
    }

    for (let i = 0; i < this.particles.length; i++) {
      const part = this.particles[i];

      // world position in svg user coords
      const px = part.baseUx + part.x;
      const py = part.baseUy + part.y;

      let dx = px - mouseUx;
      let dy = py - mouseUy;
      const distSq = dx * dx + dy * dy;
      const dist = Math.sqrt(distSq) || 0.0001;

      let fx = 0;
      let fy = 0;
      if (dist < influenceRadius) {
        const fall = 1 - dist / influenceRadius; // 1 near mouse -> 0 at radius
        // Use particle's individual sensitivity and mass
        const repulsionStrength = baseRepulsion * part.sensitivity;
        const force = (repulsionStrength * fall) / (dist + 20);
        fx = (dx / dist) * force;
        fy = (dy / dist) * force;
      }

      // spring force back to origin in user units - use individual spring constant
      const sfx = -part.springK * part.x;
      const sfy = -part.springK * part.y;

      const ax = (fx + sfx) / part.mass; // divide by mass for varied acceleration
      const ay = (fy + sfy) / part.mass;

      part.vx += ax * dt;
      part.vy += ay * dt;

      // frame-rate independent damping - use individual damping
      part.vx *= Math.pow(part.damping, dt * 60);
      part.vy *= Math.pow(part.damping, dt * 60);

      part.x += part.vx * dt;
      part.y += part.vy * dt;

      // clamp displacement so it can't be pulled off-screen / too far
      const dispMag = Math.hypot(part.x, part.y);
      if (dispMag > maxDisp) {
        const scale = maxDisp / dispMag;
        part.x *= scale;
        part.y *= scale;
        part.vx *= 0.4;
        part.vy *= 0.4;
      }

      // write transform in SVG user units
      part.el.setAttribute("transform", `translate(${part.x.toFixed(2)},${part.y.toFixed(2)})`);
    }

    // Y-axis parallax scroll effect for the whole SVG pattern
    if (this.svgPattern) {
      const baseOffsetPercent = -30; // -30% base offset for centering
      const maxScrollParallax = 300; // px - how much the SVG moves vertically with parallax
      const scrollOffset = (this.scrollProg - 0.5) * 2 * maxScrollParallax; // -300..300

      // Apply both base offset (%) and parallax offset (px) as CSS transform
      (this.svgPattern as any).style.transform = `translateY(calc(${baseOffsetPercent}% + ${scrollOffset}px))`;
    }

    this.rafId = requestAnimationFrame(this.animate);
  }

  public destroy() {
    document.removeEventListener("mousemove", this.onPointer);
    document.removeEventListener("touchmove", this.onPointer);
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener("resize", this.recomputeBasePositions);

    ScrollTrigger.getAll().forEach((st) => {
      if (st.trigger === this.blockquote) st.kill();
    });

    // snap particles back to origin using GSAP attr tween (SVG transform attr)
    for (const p of this.particles) {
      gsap.to(p.el, {
        attr: { transform: "translate(0,0)" },
        duration: 0.45,
        ease: "power2.out",
      });
      p.el.style.willChange = "";
    }

    gsap.killTweensOf(this.topSvg as any);
    gsap.killTweensOf(this.bottomSvg as any);
    gsap.killTweensOf(this.svgPattern as any);
  }
}

// initialize multiple instances
function initBlockQuotes() {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(".quoteBlock"));
  if (nodes.length === 0) return;
  const instances: BlockQuote[] = [];
  for (const el of nodes) {
    const instance = new BlockQuote(el);
    instance.init();
    instances.push(instance);
  }
  (window as any)._blockQuoteInstances = instances;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBlockQuotes);
} else {
  initBlockQuotes();
}